import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { PMComponentRef } from '@axilon-apps/domains-ontology-ui-state'

import { LazyChatMarkdown } from '../ChatMarkdown/LazyChatMarkdown'
import { componentChipHref } from '../primitives/ComponentChip'
import {
  EVENT_ACCENT_BORDER_CLASS,
  EVENT_PILL_CLASS,
  type SummaryEventTone
} from '../../lib/summary-event-tone'
import { tagPathLeaf } from '../../lib/tag-path-leaf'
import { formatEventWindowUTC } from '../../lib/time'
import { cn } from '../../lib/utils'

export interface SummaryReportMetric {
  label: string
  value: number
  unit?: string
  deltaVsPriorPeriod?: number
}

export interface SummaryReportEvent {
  occurredAt?: string
  description: string
  severity?: SummaryEventTone
  evidence: readonly PMComponentRef[]
}

const NARRATIVE_MARKDOWN_CLASS = [
  'text-xs leading-relaxed text-ink-2',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:m-0 [&_p+p]:mt-2',
  '[&_h1]:mt-3.5 [&_h1]:mb-1.5 [&_h1]:text-13 [&_h1]:font-semibold [&_h1]:leading-snug [&_h1]:tracking-tight [&_h1]:text-ink',
  '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-xs [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:text-ink',
  '[&_h3]:mt-2.5 [&_h3]:mb-1 [&_h3]:text-11 [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-ink',
  '[&_h1:first-child]:mt-0 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0',
  '[&_ul]:my-2 [&_ol]:my-2 [&_li]:mt-0.5 [&_li]:leading-relaxed',
  '[&_strong]:font-semibold [&_strong]:text-ink',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_blockquote]:text-ink-3',
  '[&_hr]:my-2.5 [&_hr]:border-line',
  '[&_code]:rounded-sm [&_code]:bg-bg-sunk [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-11',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-line [&_pre]:bg-bg-sunk [&_pre]:p-2.5 [&_pre]:text-11',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline',
  '[&_table]:my-2 [&_table]:w-full [&_table]:text-11',
  '[&_th]:border-b [&_th]:border-line [&_th]:py-1 [&_th]:text-left [&_th]:font-medium [&_th]:text-ink-3',
  '[&_td]:border-b [&_td]:border-line [&_td]:py-1'
].join(' ')

export function SummaryReportSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
        {label}
      </h3>
      {children}
    </section>
  )
}

/**
 * Narrative, metrics table, and notable events — shared by Insights and the Run
 * page. A review surface supplies `renderEventAction` for top-right card chrome
 * and `renderEventFooter` for anything it expands below; the Run page passes
 * neither and stays read-only.
 */
export function SummaryReportSections<TEvent extends SummaryReportEvent>({
  narrative,
  metrics,
  notableEvents,
  renderEventAction,
  renderEventFooter,
  children
}: {
  narrative: string
  metrics: readonly SummaryReportMetric[]
  notableEvents: readonly TEvent[]
  renderEventAction?: (event: TEvent, index: number) => ReactNode
  renderEventFooter?: (event: TEvent, index: number) => ReactNode
  children?: ReactNode
}) {
  return (
    <>
      {narrative ? (
        <SummaryReportSection label="Summary">
          <div className="rounded-lg border border-line bg-bg-elev/40 px-4 py-3.5">
            <LazyChatMarkdown className={NARRATIVE_MARKDOWN_CLASS}>{narrative}</LazyChatMarkdown>
          </div>
        </SummaryReportSection>
      ) : null}

      {metrics.length > 0 ? (
        <SummaryReportSection label="Metrics">
          <SummaryMetricsTable metrics={metrics} />
        </SummaryReportSection>
      ) : null}

      <SummaryReportSection label="Notable events">
        {notableEvents.length === 0 ? (
          <p className="m-0 text-sm text-ink-3">Nothing notable this period.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {notableEvents.map((event, index) => (
              <NotableEventRow
                key={`${event.occurredAt ?? 'event'}-${index}`}
                event={event}
                action={renderEventAction?.(event, index)}
                footer={renderEventFooter?.(event, index)}
              />
            ))}
          </ol>
        )}
      </SummaryReportSection>

      {children}
    </>
  )
}

/**
 * Chip box shared by the severity pill and any review chrome a caller renders in
 * the event header row, so the two stay the same height and sit level.
 */
export const EVENT_HEADER_CHIP_CLASS =
  'inline-flex h-5 items-center rounded-full px-2 text-10 font-semibold uppercase leading-none tracking-wide'

function formatMetricValue(value: number): string {
  return value.toLocaleString(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 4
  })
}

function formatDelta(delta: number): string {
  const abs = formatMetricValue(Math.abs(delta))
  if (delta > 0) return `+${abs}`
  if (delta < 0) return `\u2212${abs}`
  return '0'
}

function deltaClass(delta: number | undefined): string {
  if (delta === undefined || delta === 0) return 'text-ink-3'
  return delta > 0 ? 'text-st-run' : 'text-st-down'
}

function SummaryMetricsTable({ metrics }: { metrics: readonly SummaryReportMetric[] }) {
  const showDelta = metrics.some((metric) => metric.deltaVsPriorPeriod !== undefined)
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-bg-elev/40">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-line bg-bg-sunk/50">
            <th className="px-3 py-2 text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              Metric
            </th>
            <th className="px-3 py-2 text-right text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              Value
            </th>
            {showDelta ? (
              <th className="px-3 py-2 text-right text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
                vs prior
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric, index) => {
            const delta = metric.deltaVsPriorPeriod
            return (
              <tr
                key={`${metric.label}-${index}`}
                className={cn(index > 0 && 'border-t border-line/60')}
              >
                <td className="px-3 py-2 text-xs font-medium text-ink">{metric.label}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums text-ink">
                  {formatMetricValue(metric.value)}
                  {metric.unit ? <span className="ml-1 text-ink-3">{metric.unit}</span> : null}
                </td>
                {showDelta ? (
                  <td
                    className={cn('px-3 py-2 text-right text-xs tabular-nums', deltaClass(delta))}
                  >
                    {delta !== undefined ? formatDelta(delta) : '\u2014'}
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function NotableEventRow({
  event,
  action,
  footer
}: {
  event: SummaryReportEvent
  action?: ReactNode
  footer?: ReactNode
}) {
  const severity: SummaryEventTone = event.severity ?? 'info'
  const when = event.occurredAt ? formatEventWindowUTC(event.occurredAt) : ''
  return (
    <li
      className={cn(
        'min-w-0 overflow-hidden rounded-lg border border-line border-l-2 bg-bg-elev/40 px-3 py-3',
        EVENT_ACCENT_BORDER_CLASS[severity]
      )}
    >
      {/* items-start keeps the action chip on the pill's line when the timestamp wraps. */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={cn(EVENT_HEADER_CHIP_CLASS, EVENT_PILL_CLASS[severity])}>
            {severity}
          </span>
          {when ? <time className="text-11 tabular-nums text-ink-3">{when}</time> : null}
        </div>
        {action ? <div className="flex shrink-0">{action}</div> : null}
      </div>
      <p className="m-0 mt-2 text-sm leading-relaxed text-ink-2">{event.description}</p>
      {event.evidence.length > 0 ? (
        <ul className="m-0 mt-2.5 flex list-none flex-wrap gap-1.5 p-0">
          {event.evidence.map((component) => (
            <li key={component.id} className="min-w-0 max-w-full">
              <EvidenceTagChip component={component} />
            </li>
          ))}
        </ul>
      ) : null}
      {footer}
    </li>
  )
}

function EvidenceTagChip({ component }: { component: PMComponentRef }) {
  const full = (component.name?.trim() || component.id).trim()
  const label = tagPathLeaf(full)
  const title = full
  const chipClass =
    'inline-flex max-w-[14rem] min-w-0 items-center overflow-hidden rounded-md border border-line bg-bg px-2 py-0.5 font-mono text-11 leading-tight text-ink-2'
  const inner = <span className="min-w-0 truncate">{label}</span>

  if (component.linkable === false) {
    return (
      <span className={chipClass} title={title}>
        {inner}
      </span>
    )
  }

  return (
    <Link
      to={componentChipHref(component)}
      className={cn(chipClass, 'hover:border-brand')}
      title={title}
    >
      {inner}
    </Link>
  )
}
